import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Gift, Mail, MessageCircle, Link2, UserPlus, CreditCard, Sparkles, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const REF_KEY = "jt_ref";
export const storedRef = () => { try { return (localStorage.getItem(REF_KEY) || "").toUpperCase(); } catch (e) { return ""; } };

export const RefCapture = () => {
  const { search } = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(search).get("ref");
    if (ref) try { localStorage.setItem(REF_KEY, ref.trim().toUpperCase()); } catch (e) { /* storage blocked */ }
  }, [search]);
  return null;
};

export const redeemFreeSession = async () => {
  try {
    const { data } = await api.post("/one-to-one/redeem-free");
    window.location.href = `/payment/success?session_id=${data.session_id}`;
    return true;
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not book");
    return false;
  }
};

export const ShareButtons = ({ code, link }) => {
  const msg = `Learn to read music with Jason Thompson at The JT Hub. Use my code ${code} for £50 off your first course: ${link}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Invite link copied"));
  const cls = "rounded-full border-border";
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="referral-share-buttons">
      <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="referral-share-whatsapp">
        <Button variant="outline" size="sm" className={cls}><MessageCircle size={14} className="mr-1.5" /> WhatsApp</Button>
      </a>
      <a href={`mailto:?subject=${encodeURIComponent("£50 off a music course at The JT Hub")}&body=${encodeURIComponent(msg)}`} data-testid="referral-share-email">
        <Button variant="outline" size="sm" className={cls}><Mail size={14} className="mr-1.5" /> Email</Button>
      </a>
      <Button onClick={copy} variant="outline" size="sm" className={cls} data-testid="referral-share-copy"><Link2 size={14} className="mr-1.5" /> Copy link</Button>
    </div>
  );
};

const Step = ({ icon: Icon, value, label, testId, active }) => (
  <div className={`flex-1 rounded-xl border p-3 text-center transition-colors ${active ? "border-primary/50 bg-primary/10" : "border-border bg-background"}`} data-testid={testId}>
    <Icon size={16} className="mx-auto text-primary" />
    <p className="mt-1 font-mono text-xl text-foreground">{value}</p>
    <p className="text-[11px] text-muted-foreground">{label}</p>
  </div>
);

export const ReferralTracker = ({ stats }) => {
  const earned = stats.conversions;
  return (
    <div className="mt-5" data-testid="referral-tracker">
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Your referrals</p>
      <div className="mt-2 flex items-stretch gap-2">
        <Step icon={UserPlus} value={stats.invited || 0} label="Friends invited" testId="tracker-invited" active={stats.invited > 0} />
        <Step icon={CreditCard} value={stats.conversions || 0} label="Friends paid" testId="tracker-paid" active={stats.conversions > 0} />
        <Step icon={Gift} value={earned} label={`Free lessons earned${stats.free_used ? ` · ${stats.free_used} used` : ""}`} testId="tracker-earned" active={earned > 0} />
      </div>
    </div>
  );
};

export const FreeLessonBanner = () => {
  const [n, setN] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/referral/stats").then(({ data }) => setN(data.free_sessions || 0)).catch(() => {}); }, []);
  if (n < 1) return null;
  const book = async () => { setBusy(true); if (!(await redeemFreeSession())) setBusy(false); };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-primary/50 bg-primary/10 px-5 py-4" data-testid="free-lesson-banner">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Sparkles size={18} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-lg text-foreground">Your free 1:1 is ready — book now</p>
        <p className="text-sm text-muted-foreground">You have {n} free lesson{n > 1 ? "s" : ""} with Jason as a thank you for referring a friend.</p>
      </div>
      <Button onClick={book} disabled={busy} data-testid="free-lesson-book" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
        <CalendarCheck size={15} className="mr-1.5" /> Book my free 1:1
      </Button>
    </div>
  );
};
